import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import AdblockService from '../../../services/adblock';
import { 
  AdblockFilters, 
  AdblockSummary, 
  AdblockDailyMetrics 
} from '../../../types/adblock';// Importation des composants UI
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Separator } from '../../ui/separator';
import { Skeleton } from '../../ui/skeleton';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { AlertCircle, BarChart3, Filter } from 'lucide-react';

// Composants spécialisés pour Adblock
import { AdblockFiltersComponent } from './AdblockFilters';
import { AdblockSummaryCards } from './AdblockSummary';
import { AdblockCharts } from './AdblockCharts';
import { AdblockTable } from './AdblockTable';
import { AdblockNoService } from './AdblockNoService';

export function AdblockDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  
  // États
  const [filters, setFilters] = useState<AdblockFilters>({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    domains: [],
    devices: [] // Vide par défaut = tous les devices
  });
  
  const [summary, setSummary] = useState<AdblockSummary>({
    totalPageviews: 0,
    totalPageviewsBlockingAds: 0,
    totalPageviewsBlockingAcceptableAds: 0,
    totalRequests: 0,
    totalImpressions: 0,
    totalClicks: 0,
    totalRevenueUSD: 0,
    totalPublisherRevenueNet: 0,
    totalAdServingFees: 0,
    averageCommissionRate: 0,
    blockingRate: 0,
    acceptableAdsBlockingRate: 0
  });
  
  const [dailyMetrics, setDailyMetrics] = useState<AdblockDailyMetrics[]>([]);
  const [availableDomains, setAvailableDomains] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNoService, setHasNoService] = useState(false);

  // Vérification de l'authentification
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [navigate, currentUser]);

  // Charger les domaines disponibles
  useEffect(() => {
    const loadDomains = async () => {
      try {
        const domains = await AdblockService.getDomains();
        setAvailableDomains(domains);
        
        // Si aucun domaine n'est disponible pour un utilisateur non-admin, 
        // cela signifie qu'aucun service AdblockWorkaround n'est configuré
        if (domains.length === 0 && currentUser?.role !== 'ADMIN') {
          setHasNoService(true);
        } else {
          setHasNoService(false);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des domaines:', error);
        setError(t('adblock.dashboard.error'));
      }
    };

    if (currentUser) {
      loadDomains();
    }
  }, [currentUser, t]);

  // Charger les métriques
  const loadMetrics = async (newFilters?: AdblockFilters) => {
    const filtersToUse = newFilters || filters;
    setIsLoadingData(true);
    setError(null);

    try {
      const response = await AdblockService.getMetrics(filtersToUse);
      setSummary(response.summary);
      setDailyMetrics(response.dailyMetrics);
    } catch (error) {
      console.error('Erreur lors du chargement des métriques:', error);
      setError(t('adblock.dashboard.error'));
    } finally {
      setIsLoadingData(false);
    }
  };

  // Charger les métriques initiales
  useEffect(() => {
    if (currentUser && availableDomains.length >= 0) {
      loadMetrics();
    }
  }, [currentUser, availableDomains]);

  // Gérer le changement de filtres
  const handleFiltersChange = (newFilters: AdblockFilters) => {
    setFilters(newFilters);
    loadMetrics(newFilters);
  };

  // Simuler le chargement initial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  if (!currentUser) return null;

  return (
    <div className="min-h-[calc(100vh-7rem)] overflow-auto">
      <div className="p-6 space-y-6 animate-in fade-in duration-500">
      {/* En-tête */}
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold">{t('adblock.dashboard.title')}</h1>
          </div>
          <div className="ml-4 flex-grow">
            <Separator />
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          {t('adblock.dashboard.description')}
        </p>
      </div>

      {/* Affichage du composant "No Service" si aucun service n'est configuré */}
      {!isLoading && hasNoService && (
        <AdblockNoService />
      )}

      {/* Contenu normal du dashboard (filtres, métriques, etc.) */}
      {!isLoading && !hasNoService && (
        <>
      {/* Filtres */}
      <div className="animate-in fade-in duration-500 delay-300">
        {isLoading ? (
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                {t('common.filters')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                {t('common.filters')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AdblockFiltersComponent
                filters={filters}
                availableDomains={availableDomains}
                onChange={handleFiltersChange}
                isLoading={isLoadingData}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Affichage des erreurs */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Résumé des métriques */}
      <Card className="animate-in fade-in duration-500 delay-500">
        <CardHeader>
          <CardTitle>{t('adblock.dashboard.summary.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <AdblockSummaryCards
            summary={summary}
            isLoading={isLoading || isLoadingData}
          />
        </CardContent>
      </Card>

      {/* Graphiques */}
      <div className="animate-in fade-in duration-500 delay-700">
        <AdblockCharts
          dailyMetrics={dailyMetrics}
          isLoading={isLoading || isLoadingData}
          theme={theme}
        />

        {/* Tableau de données quotidiennes */}
        <div className="mt-8">
          <AdblockTable
            dailyMetrics={dailyMetrics}
            isLoading={isLoading || isLoadingData}
          />
        </div>
      </div>

      {/* Message si pas de données */}
      {!isLoading && !isLoadingData && dailyMetrics.length === 0 && !error && (
        <div className="text-center py-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
              <BarChart3 className="h-8 w-8 text-gray-400" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('adblock.dashboard.noData')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md">
                {t('adblock.dashboard.description')}
              </p>
            </div>
            <Button onClick={() => loadMetrics()} variant="outline">
              {t('adblock.dashboard.retry')}
            </Button>
          </div>
        </div>
      )}
      </>
      )}
      </div>
    </div>
  );
}